import { Component, effect, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { firstValueFrom, of, switchMap } from 'rxjs';

import { AskTemplatesDataService } from '@core/ask-hr-templates-logic';
import { TicketService } from '@core/ask-hr-tickets-logic';
import type { CreateAskTicket } from '@core/ask-hr-tickets-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { ButtonComponent } from '@core/user-interface-legacy';

import type { AskHrDynamicFormConfig } from '../../ask-templates-form.service';
import { CreateTicketComponent } from '../../create-ticket/create-ticket.component';

@Component({
  selector: 'ask-chat-request-form',
  template: `
    @if (selectedTemplate(); as template) {
      <tlc-create-ticket
        #createTicketComponent
        [template]="template"
        (ticketChanges)="ticketData.set($event)"
      />
      <div layout layout-space-between margin-top-medium margin-bottom>
        <rt-button variant="plain" (click)="cancelClick.emit()">
          {{ 'tlc.common.cancel' | translate }}
        </rt-button>
        <rt-button
          variant="primary"
          (click)="submit()"
          [disabled]="!createTicketComponent.isFormValid()"
        >
          {{ 'tlc.ask_hr.forms.chat.submit' | translate }}
        </rt-button>
      </div>
    }
  `,
  styleUrls: ['./chat-request-form.component.less'],
  imports: [TranslatePipe, ButtonComponent, CreateTicketComponent],
})
export class ChatRequestFormComponent {
  private readonly templateDataService = inject(AskTemplatesDataService);
  private readonly ticketService = inject(TicketService);

  public templateId = input<number>();

  public cancelClick = output<void>();
  public submitted = output<void>();

  protected ticketData = signal<CreateAskTicket<AskHrDynamicFormConfig> | null>(null);
  protected selectedTemplate = toSignal(
    toObservable(this.templateId).pipe(
      switchMap(id => (id ? this.templateDataService.getTemplateById(id) : of(null))),
    ),
    { initialValue: null },
  );

  private loadTemplate = effect(() => {
    const id = this.templateId();
    if (!id) return;

    this.templateDataService.loadTemplateById(id).subscribe();
  });

  protected async submit() {
    const ticketData = this.ticketData();
    if (!ticketData) return;

    const result = await firstValueFrom(this.ticketService.createTicket(ticketData));
    if (result.isSuccess) {
      this.submitted.emit();
    }
  }
}
